import React from 'react';
import { useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import {
  AttributesField as AttributesFieldType,
  FormField as FormFieldType,
} from '~/types/SSOForm';

type AttributesValue = {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  department?: string;
  role?: string;
};

type Props = {
  formField: FormFieldType<AttributesFieldType>;
  onExplain?: (fieldId: string) => void;
  onChange: (value: AttributesValue) => void;
};

export const UserAttributesField = ({
  formField,
  onExplain,
  onChange,
}: Props) => {
  const { email, firstName, lastName, jobTitle, department, role } =
    formField.field.value;

  const isPremiumAdministrationEnabled = useFlag(
    FlagType.PremiumAdministration,
  );

  const intl = useIntl();

  const textOrUrlPlaceholder = intl.formatMessage({
    id: 'UserAttributesField.textOrUrlPlaceholder',
    defaultMessage: 'Text or URL',
    description: 'Placeholder for text or URL input',
  });

  const premiumFeatureTooltip = intl.formatMessage({
    id: 'UserAttributesField.premiumFeatureTooltip',
    defaultMessage: 'Available with the Advanced Administration add-on.',
    description: 'Tooltip for premium features',
  });

  return (
    <Layout direction="column" minW="input.min-width" spacing={1} w="100%">
      <Layout direction="column">
        <Layout>
          <Text variant="section">{formField.title}</Text>
          {onExplain && (
            <Button
              text={intl.formatMessage({
                id: 'UserAttributesField.explainButton',
                defaultMessage: 'Explain this',
                description: 'Button text to explain the field',
              })}
              variant="action"
              onClick={() => onExplain(formField.fieldName)}
            />
          )}
        </Layout>
        <Text preset="description">{formField.description}</Text>
      </Layout>

      <Layout direction="column" spacing={4}>
        <FormField
          input={TextInput}
          label={intl.formatMessage({
            id: 'UserAttributesField.emailLabel',
            defaultMessage: 'Email',
            description: 'Label for email field',
          })}
          placeholder={textOrUrlPlaceholder}
          name="email"
          description={intl.formatMessage({
            id: 'UserAttributesField.emailDescription',
            defaultMessage: 'This attribute is used to sign in the user.',
            description: 'Description for email field',
          })}
          value={email}
          error={
            email === ''
              ? intl.formatMessage({
                  id: 'UserAttributesField.emailRequiredError',
                  defaultMessage: 'Email is required',
                  description: 'Error message when email is required',
                })
              : undefined
          }
          onChange={(email) =>
            onChange({ ...formField.field?.value, email: email as string })
          }
        ></FormField>
        <FormField
          input={TextInput}
          label={intl.formatMessage({
            id: 'UserAttributesField.firstNameLabel',
            defaultMessage: 'First name',
            description: 'Label for first name field',
          })}
          placeholder={textOrUrlPlaceholder}
          name="firstName"
          error={
            firstName === ''
              ? intl.formatMessage({
                  id: 'UserAttributesField.firstNameRequiredError',
                  defaultMessage: 'First name is required',
                  description: 'Error message when first name is required',
                })
              : undefined
          }
          value={firstName}
          onChange={(firstName) =>
            onChange({
              ...formField.field?.value,
              firstName: firstName as string,
            })
          }
        ></FormField>
        <FormField
          input={TextInput}
          label={intl.formatMessage({
            id: 'UserAttributesField.lastNameLabel',
            defaultMessage: 'Last name',
            description: 'Label for last name field',
          })}
          placeholder={textOrUrlPlaceholder}
          name="lastName"
          error={
            lastName === ''
              ? intl.formatMessage({
                  id: 'UserAttributesField.lastNameRequiredError',
                  defaultMessage: 'Last name is required',
                  description: 'Error message when last name is required',
                })
              : undefined
          }
          value={lastName}
          onChange={(lastName) =>
            onChange({
              ...formField.field?.value,
              lastName: lastName as string,
            })
          }
        ></FormField>
        <FormField
          input={TextInput}
          label={intl.formatMessage({
            id: 'UserAttributesField.jobTitleLabel',
            defaultMessage: 'Job title',
            description: 'Label for job title field',
          })}
          placeholder={textOrUrlPlaceholder}
          name="jobTitle"
          value={jobTitle}
          optional={true}
          disabled={!isPremiumAdministrationEnabled}
          tooltip={
            isPremiumAdministrationEnabled ? undefined : premiumFeatureTooltip
          }
          onChange={(jobTitle) =>
            onChange({
              ...formField.field?.value,
              jobTitle: jobTitle as string,
            })
          }
        ></FormField>
        <FormField
          input={TextInput}
          label={intl.formatMessage({
            id: 'UserAttributesField.departmentLabel',
            defaultMessage: 'Department',
            description: 'Label for department field',
          })}
          placeholder={textOrUrlPlaceholder}
          name="department"
          value={department}
          optional={true}
          disabled={!isPremiumAdministrationEnabled}
          tooltip={
            isPremiumAdministrationEnabled ? undefined : premiumFeatureTooltip
          }
          onChange={(department) =>
            onChange({
              ...formField.field?.value,
              department: department as string,
            })
          }
        ></FormField>
        <FormField
          input={TextInput}
          label={intl.formatMessage({
            id: 'UserAttributesField.roleLabel',
            defaultMessage: 'Role',
            description: 'Label for role field',
          })}
          placeholder={textOrUrlPlaceholder}
          name="role"
          value={role}
          optional={true}
          disabled={!isPremiumAdministrationEnabled}
          tooltip={
            isPremiumAdministrationEnabled ? undefined : premiumFeatureTooltip
          }
          onChange={(role) =>
            onChange({ ...formField.field?.value, role: role as string })
          }
        ></FormField>
      </Layout>
    </Layout>
  );
};
